/**
 * Phase C — Canonical Operator Flow (console projection)
 *
 * Pure, declarative description of the canonical operator verb surface shipped
 * in slice `phase-c-cli-canonicalization-v1`. This module is intentionally
 * side-effect free and contains NO execution logic, NO governance logic, and
 * NO client-side authority. It exists solely so the console can project the
 * same canonical control-plane language as the CLI.
 *
 * Any change to the canonical verb set MUST remain synchronized with:
 *   - `scripts/xable.mjs` (CANONICAL_VERB_TARGETS)
 *   - `src/trustgate/cli/governanceValidator.ts` (allowedTopLevelCommands)
 *
 * See: `docs/phase-c-control-plane-v1.md`
 *      `docs/phase-c-ui-console-canonical-flows-v1.md`
 */

export interface CanonicalVerb {
  /** Canonical verb name (exact CLI token). */
  readonly verb: string;
  /** Short operator-facing description (single line, no marketing copy). */
  readonly description: string;
  /** Legacy alias this canonical verb rewrites to in CLI dispatch (or self for run/verify/closeout). */
  readonly routesTo: string;
}

/**
 * Canonical operator flow order. Changing this order is a governance-visible
 * change and must be reflected in the CLI help output as well.
 */
export const CANONICAL_FLOW_VERBS: readonly CanonicalVerb[] = Object.freeze([
  { verb: 'start',    description: 'Initialize a task / session',          routesTo: 'init-task' },
  { verb: 'propose',  description: 'Generate ranked proposals',            routesTo: 'suggest-next' },
  { verb: 'choose',   description: 'Select the proposal to execute',       routesTo: 'ws-choose' },
  { verb: 'run',      description: 'Execute through governed paths',       routesTo: 'run' },
  { verb: 'verify',   description: 'Inspect and validate outputs',         routesTo: 'verify' },
  { verb: 'closeout', description: 'Finalize artifacts and receipts',      routesTo: 'build-loop-closeout' },
]);

/**
 * Legacy → canonical hints surfaced alongside the canonical flow so operators
 * can recognize deprecated command language already used in other surfaces.
 * This mirrors the CLI `LEGACY_CANONICAL_HINTS` map.
 */
export const LEGACY_CANONICAL_HINTS: ReadonlyArray<{ legacy: string; canonical: string }> = Object.freeze([
  { legacy: 'init-task',    canonical: 'start' },
  { legacy: 'suggest-next', canonical: 'propose' },
  { legacy: 'ws-next',      canonical: 'propose' },
  { legacy: 'ws-choose',    canonical: 'choose' },
]);
